/**
 * Track graph + path sampling utilities for the OHT simulation.
 *
 * Rails are turned into a connected graph:
 *  - nodes   = rail endpoints clustered by proximity (shared junctions)
 *  - edges   = one per rail segment, carrying a sampled world-space centreline
 *
 * The simulation moves vehicles along edges by arc length and, at each node,
 * picks the outgoing edge that best continues the current heading so a vehicle
 * naturally follows a loop.
 */
import { LayoutElement } from '../types';
import {
  Pt,
  ALL_RAIL_TYPES,
  ENDPOINT_TOLERANCE,
  resolveRailType,
  getCurveRadius,
  getCurveSweepAngle,
  getCurveSweepSign,
  isCurveRailType,
  isJunctionRailType,
  getCircleJunctionMaxPorts,
  getCircleJunctionSpokesLocal,
  getCircleJunctionActivePortIndices,
  isTrackUnidirectional,
} from './topology';
import { getConnectorFarLegMid, getConnectorHomeLegMid, isConnectorElement } from './connectorRail';

export interface TrackNode {
  id: number;
  x: number;
  y: number;
}

export interface TrackEdge {
  id: string; // rail element id
  a: number; // node index at centreline start
  b: number; // node index at centreline end
  points: Pt[]; // world-space centreline, ordered a -> b
  cumLen: number[]; // cumulative length at each point (cumLen[0] = 0)
  length: number;
  /** Offset of this segment's start from the rail element's logical start. */
  cumStart: number;
  /** Optional switch behavior copied from rail custom properties (merge/diverge). */
  switchMode?: string;
  isUnidirectional?: boolean;
  /**
   * - roundabout: legacy ring-arc (unused for new junctions)
   * - turntable: radial tip→hub stub on a rotating circle junction
   * - connector: H-connector home↔far bridge (AMHS export / backend routing;
   *   browser local walkers skip these so OHTs don't patrol the crossbar)
   */
  pathKind?: 'roundabout' | 'turntable' | 'connector';
  /** Port index on a junction-circle turntable stub. */
  portIndex?: number;
}

/** Slow-down while riding the roundabout ring (realistic turn). */
export const ROUNDABOUT_SPEED_FACTOR = 0.65;

/** Traffic rule on a circular junction ring. */
export type RoundaboutDirection = 'cw' | 'ccw' | 'shortest';

export function getRoundaboutDirection(el: LayoutElement): RoundaboutDirection {
  const raw = el.customProperties?.roundaboutDirection;
  if (raw === 'cw' || raw === 'ccw' || raw === 'shortest') return raw;
  // Fixed circulation by default — avoids lane-crossing opposite arcs.
  return 'ccw';
}

export interface TrackGraph {
  nodes: TrackNode[];
  edges: TrackEdge[];
  /** For each node, the indices of edges incident to it. */
  nodeEdges: number[][];
}

/**
 * Get the world-space centerline of a single rail.
 * For curves, samples the arc; for straight rails, returns midline endpoints.
 * @param el - Rail element
 * @returns Array of world-space points from rail start to end
 */
export function getRailCenterlinePoints(el: LayoutElement): Pt[] {
  const { x, y } = el.position;
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);

  const toWorld = (lx: number, ly: number): Pt => ({
    x: x + lx * cos - ly * sin,
    y: y + lx * sin + ly * cos,
  });

  const railType = resolveRailType(el);

  if (isCurveRailType(railType)) {
    const radius = getCurveRadius(w, h);
    const sweep = getCurveSweepSign(el) * getCurveSweepAngle(railType);
    const segments = Math.max(8, Math.ceil(Math.abs(sweep) / 5));
    const points: Pt[] = [];
    for (let i = 0; i <= segments; i++) {
      const rad = ((sweep * i) / segments) * (Math.PI / 180);
      points.push(toWorld(radius * Math.cos(rad), radius * Math.sin(rad)));
    }
    return points;
  }

  // H-connector transfer path: home mid → cross → far mid (single polyline for sampling).
  if (railType === 'connector') {
    return [
      toWorld(w / 2, 0),
      toWorld(w / 2, h),
    ];
  }

  // Straight rail: midpoints of the two short edges.
  return [toWorld(0, h / 2), toWorld(w, h / 2)];
}

/** World-space centerline segments for rails that render as multiple branches. */
function getRailCenterlineSegments(el: LayoutElement): { points: Pt[]; cumStart: number }[] {
  const { x, y } = el.position;
  const w = el.dimensions.width;
  const h = el.dimensions.height;
  const r = ((el.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const toWorld = (lx: number, ly: number): Pt => ({
    x: x + lx * cos - ly * sin,
    y: y + lx * sin + ly * cos,
  });
  const railType = resolveRailType(el);

  if (railType === 'connector') {
    const home = [toWorld(0, 0), toWorld(w, 0)];
    const cross = [toWorld(w / 2, 0), toWorld(w / 2, h)];
    const far = [toWorld(0, h), toWorld(w, h)];
    const homeLen = Math.hypot(home[1].x - home[0].x, home[1].y - home[0].y);
    const crossLen = Math.hypot(cross[1].x - cross[0].x, cross[1].y - cross[0].y);
    return [
      { points: home, cumStart: 0 },
      { points: cross, cumStart: homeLen },
      { points: far, cumStart: homeLen + crossLen },
    ];
  }

  // Circular junctions are wired as port-to-port through-paths in
  // buildTrackGraph, so they never reach here.
  return [{ points: getRailCenterlinePoints(el), cumStart: 0 }];
}

/**
 * Roundabout paths ride the outer ring — never the hub.
 *
 * Path = tip → C1 blend onto ring → circular arc → C1 blend off → tip.
 * That gives continuous heading (no instant 45°/90° pivots) while supporting
 * every active port pair (T1→E1 … T3→E3) without hardcoded routes.
 */
const JUNCTION_BLEND_SAMPLES = 48;
const JUNCTION_ARC_STEP_RAD = Math.PI / 48; // ~3.75°

function sampleCubicBezier(p0: Pt, p1: Pt, p2: Pt, p3: Pt, samples: number): Pt[] {
  const points: Pt[] = [];
  const n = Math.max(1, samples);
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    const b0 = u * u * u;
    const b1 = 3 * u * u * t;
    const b2 = 3 * u * t * t;
    const b3 = t * t * t;
    points.push({
      x: b0 * p0.x + b1 * p1.x + b2 * p2.x + b3 * p3.x,
      y: b0 * p0.y + b1 * p1.y + b2 * p2.y + b3 * p3.y,
    });
  }
  return points;
}

function sampleCircularArc(
  center: Pt,
  radius: number,
  theta0: number,
  sweep: number,
  samples: number
): Pt[] {
  const points: Pt[] = [];
  const n = Math.max(1, samples);
  for (let i = 0; i <= n; i++) {
    const th = theta0 + (sweep * i) / n;
    points.push({
      x: center.x + radius * Math.cos(th),
      y: center.y + radius * Math.sin(th),
    });
  }
  return points;
}

/** Shortest signed sweep from θa to θb in (−π, π]. */
function shortestSweepRad(thetaA: number, thetaB: number): number {
  let d = thetaB - thetaA;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d <= -Math.PI) d += 2 * Math.PI;
  return d;
}

/**
 * Sweep from θa to θb honouring roundabout traffic direction.
 * - shortest: (−π, π]
 * - ccw: (0, 2π] mathematical counter-clockwise
 * - cw:  [−2π, 0) mathematical clockwise
 */
export function directedSweepRad(
  thetaA: number,
  thetaB: number,
  direction: RoundaboutDirection
): number {
  if (direction === 'shortest') return shortestSweepRad(thetaA, thetaB);

  const TWO_PI = Math.PI * 2;
  if (direction === 'ccw') {
    let d = thetaB - thetaA;
    d = ((d % TWO_PI) + TWO_PI) % TWO_PI; // [0, 2π)
    return d === 0 ? 0 : d;
  }

  // cw
  let d = thetaA - thetaB;
  d = ((d % TWO_PI) + TWO_PI) % TWO_PI; // [0, 2π) of clockwise travel
  return d === 0 ? 0 : -d;
}

function appendPath(target: Pt[], next: Pt[]) {
  for (const p of next) {
    const last = target[target.length - 1];
    if (last && Math.hypot(last.x - p.x, last.y - p.y) < 1e-6) continue;
    target.push(p);
  }
}

/**
 * Build a roundabout path between two ports.
 * Tips stay on the AABB (where attached rails snap); the vehicle blends onto
 * the ring, rides the circumference, then blends out — never through the hub.
 */
export function buildCircularJunctionRoundaboutPath(
  tipA: Pt,
  tipB: Pt,
  center: Pt,
  radius: number,
  thetaA: number,
  thetaB: number,
  direction: RoundaboutDirection = 'ccw'
): Pt[] {
  const ringA: Pt = {
    x: center.x + radius * Math.cos(thetaA),
    y: center.y + radius * Math.sin(thetaA),
  };
  const ringB: Pt = {
    x: center.x + radius * Math.cos(thetaB),
    y: center.y + radius * Math.sin(thetaB),
  };

  const sweep = directedSweepRad(thetaA, thetaB, direction);
  // Degenerate: same port.
  if (Math.abs(sweep) < 1e-6 && Math.hypot(tipA.x - tipB.x, tipA.y - tipB.y) < 1e-6) {
    return [tipA];
  }

  const sweepSign: 1 | -1 = sweep >= 0 ? 1 : -1;
  // Circle tangent for travel in the sweep direction.
  const tangentAt = (th: number): Pt => ({
    x: -Math.sin(th) * sweepSign,
    y: Math.cos(th) * sweepSign,
  });

  const stubA = Math.hypot(tipA.x - ringA.x, tipA.y - ringA.y) || 1;
  const stubB = Math.hypot(tipB.x - ringB.x, tipB.y - ringB.y) || 1;
  // Longer handles spread the radial→tangent turn so successive samples
  // never jump by ~45°/90°.
  const handle = Math.max(Math.min(stubA, stubB) * 0.85, radius * 0.65);

  const radialInA: Pt = {
    x: (ringA.x - tipA.x) / stubA,
    y: (ringA.y - tipA.y) / stubA,
  };
  const radialOutB: Pt = {
    x: (tipB.x - ringB.x) / stubB,
    y: (tipB.y - ringB.y) / stubB,
  };
  const tanA = tangentAt(thetaA);
  const tanB = tangentAt(thetaB);

  // tip → ring: leave along spoke, arrive along circle tangent (C1).
  const entry = sampleCubicBezier(
    tipA,
    { x: tipA.x + radialInA.x * handle, y: tipA.y + radialInA.y * handle },
    { x: ringA.x - tanA.x * handle, y: ringA.y - tanA.y * handle },
    ringA,
    JUNCTION_BLEND_SAMPLES
  );

  const arcSamples = Math.max(8, Math.ceil(Math.abs(sweep) / JUNCTION_ARC_STEP_RAD) || 8);
  const arc =
    Math.abs(sweep) < 1e-6
      ? [ringA, ringB]
      : sampleCircularArc(center, radius, thetaA, sweep, arcSamples);

  // ring → tip: leave along circle tangent, arrive along exit spoke (C1).
  const exit = sampleCubicBezier(
    ringB,
    { x: ringB.x + tanB.x * handle, y: ringB.y + tanB.y * handle },
    { x: tipB.x - radialOutB.x * handle, y: tipB.y - radialOutB.y * handle },
    tipB,
    JUNCTION_BLEND_SAMPLES
  );

  const points: Pt[] = [];
  appendPath(points, entry);
  appendPath(points, arc);
  appendPath(points, exit);
  return points;
}

function parseJunctionPortIndex(raw: unknown, portCount: number): number | null {
  if (raw === undefined || raw === null) return null;
  const n = Number(String(raw).replace('port-', ''));
  return Number.isInteger(n) && n >= 0 && n < portCount ? n : null;
}

/**
 * Which port pairs a vehicle may drive between.
 * Explicit `connections` win; otherwise every active port joins every other.
 * When `ordered` is true (fixed CW/CCW traffic), returns directed (i→j) pairs
 * so each leg follows the same circulation and never crosses the opposite way.
 */
export function getJunctionPortPairs(
  rail: LayoutElement,
  allRails: LayoutElement[],
  tipPts: Pt[],
  ordered: boolean
): Array<[number, number]> {
  const pairs: Array<[number, number]> = [];
  const seen = new Set<string>();

  const pushPair = (i: number, j: number) => {
    if (i === j || !tipPts[i] || !tipPts[j]) return;
    const key = ordered ? `${i}>${j}` : i < j ? `${i}-${j}` : `${j}-${i}`;
    if (seen.has(key)) return;
    seen.add(key);
    if (ordered) {
      pairs.push([i, j]);
    } else {
      pairs.push(i < j ? [i, j] : [j, i]);
    }
  };

  const connections = rail.customProperties?.connections;
  if (Array.isArray(connections) && connections.length > 0) {
    for (const conn of connections) {
      const from = parseJunctionPortIndex(
        (conn as { fromPortId?: unknown })?.fromPortId,
        tipPts.length
      );
      const to = parseJunctionPortIndex(
        (conn as { toPortId?: unknown })?.toPortId,
        tipPts.length
      );
      if (from === null || to === null) continue;
      pushPair(from, to);
      // Explicit undirected connections still need the return leg under
      // fixed circulation (separate CCW/CW arc the other way).
      if (ordered) pushPair(to, from);
    }
    if (pairs.length > 0) return pairs;
  }

  const active = Array.from(getCircleJunctionActivePortIndices(rail, allRails))
    .filter((i) => i >= 0 && i < tipPts.length)
    .sort((a, b) => a - b);
  const ports = active.length >= 2 ? active : tipPts.map((_, i) => i);

  if (ordered) {
    for (const i of ports) {
      for (const j of ports) {
        if (i !== j) pushPair(i, j);
      }
    }
  } else {
    for (let a = 0; a < ports.length; a++) {
      for (let b = a + 1; b < ports.length; b++) {
        pushPair(ports[a], ports[b]);
      }
    }
  }
  return pairs;
}

function cumulativeLengths(points: Pt[]): { cumLen: number[]; length: number } {
  const cumLen: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    const dx = points[i].x - points[i - 1].x;
    const dy = points[i].y - points[i - 1].y;
    cumLen.push(cumLen[i - 1] + Math.hypot(dx, dy));
  }
  return { cumLen, length: cumLen[cumLen.length - 1] || 0 };
}

/**
 * Builds the track graph from the current layout elements. Endpoints within
 * ENDPOINT_TOLERANCE of each other are merged into a single shared node.
 */
/**
 * Build a connected graph of rail nodes and edges for OHT routing.
 * Endpoints of rails within ENDPOINT_TOLERANCE are clustered into shared nodes.
 * @param elements - All layout elements (rails are selected and processed)
 * @returns TrackGraph with clustered nodes and edges carrying centerline points
 */
export function buildTrackGraph(elements: LayoutElement[]): TrackGraph {
  const rails = elements.filter((e) => ALL_RAIL_TYPES.has(e.type) && e.metadata.visible);

  const nodes: TrackNode[] = [];
  const tol2 = ENDPOINT_TOLERANCE * ENDPOINT_TOLERANCE;

  const findOrAddNode = (p: Pt): number => {
    for (let i = 0; i < nodes.length; i++) {
      const dx = nodes[i].x - p.x;
      const dy = nodes[i].y - p.y;
      if (dx * dx + dy * dy <= tol2) return i;
    }
    nodes.push({ id: nodes.length, x: p.x, y: p.y });
    return nodes.length - 1;
  };

  const edges: TrackEdge[] = [];

  for (const rail of rails) {
    const railType = resolveRailType(rail);
    // H-connector geometry is added after host rails are split at the leg mids
    // (see bridge edges below). Skipping here avoids treating the H outline as
    // ordinary centreline segments.
    if (isConnectorElement(rail) || railType === 'connector') continue;

    if (railType === 'junction-circle') {
      const maxPorts = getCircleJunctionMaxPorts(rail);
      const spokes = getCircleJunctionSpokesLocal(
        rail.dimensions.width,
        rail.dimensions.height,
        maxPorts
      );
      const r = ((rail.rotation || 0) * Math.PI) / 180;
      const cos = Math.cos(r);
      const sin = Math.sin(r);
      const toWorld = (lx: number, ly: number): Pt => ({
        x: rail.position.x + lx * cos - ly * sin,
        y: rail.position.y + lx * sin + ly * cos,
      });
      const hub = toWorld(rail.dimensions.width / 2, rail.dimensions.height / 2);
      const tipPts = spokes.map((s) => toWorld(s.tipX, s.tipY));

      const switchModeRaw = rail.customProperties?.mode;
      const switchMode =
        typeof switchModeRaw === 'string' ? switchModeRaw.toLowerCase() : undefined;

      // Turntable: tip → rim → hub stubs. OHT rides tip→rim, then the diameter
      // bar (rotate/cross) instead of cutting through the empty hub.
      const active = Array.from(getCircleJunctionActivePortIndices(rail, rails));
      const ports =
        active.length >= 1 ? active.filter((i) => i >= 0 && i < tipPts.length) : tipPts.map((_, i) => i);
      const hubNode = findOrAddNode(hub);
      const ringPts = spokes.map((s) => toWorld(s.ringX, s.ringY));

      for (const i of ports) {
        const tip = tipPts[i];
        const rim = ringPts[i];
        if (!tip || !rim) continue;
        const tipNode = findOrAddNode(tip);
        if (tipNode === hubNode) continue;
        const points = [tip, rim, hub];
        const { cumLen, length } = cumulativeLengths(points);
        if (length <= 0) continue;
        edges.push({
          id: rail.id,
          a: tipNode,
          b: hubNode,
          points,
          cumLen,
          length,
          cumStart: 0,
          switchMode,
          pathKind: 'turntable',
          portIndex: i,
        });
      }
      continue;
    }

    const segments = getRailCenterlineSegments(rail);
    for (const seg of segments) {
      if (seg.points.length < 2) continue;
      const { cumLen, length } = cumulativeLengths(seg.points);
      if (length <= 0) continue;

      const a = findOrAddNode(seg.points[0]);
      const b = findOrAddNode(seg.points[seg.points.length - 1]);
      const switchModeRaw = rail.customProperties?.mode;
      const switchMode =
        typeof switchModeRaw === 'string' ? switchModeRaw.toLowerCase() : undefined;
      edges.push({
        id: rail.id,
        a,
        b,
        points: seg.points,
        cumLen,
        length,
        cumStart: seg.cumStart,
        switchMode,
        isUnidirectional: isTrackUnidirectional(rail),
      });
    }
  }

  // Split host rails at H-connector attachment mids so OHT can enter/exit the bridge.
  type ConnectorAttachment = { key: string; pt: Pt };
  const attachments: ConnectorAttachment[] = [];
  for (const rail of rails) {
    if (!isConnectorElement(rail)) continue;
    attachments.push(
      { key: `${rail.id}:home`, pt: getConnectorHomeLegMid(rail) },
      { key: `${rail.id}:far`, pt: getConnectorFarLegMid(rail) }
    );
  }

  const attachTol = ENDPOINT_TOLERANCE;
  const attachTol2 = attachTol * attachTol;
  const refinedEdges: TrackEdge[] = [];
  /** Projected split-node index for each connector leg (home/far). */
  const connectorLegNodes = new Map<string, number>();

  for (const edge of edges) {
    const rail = rails.find((r) => r.id === edge.id);
    if (
      !rail ||
      isConnectorElement(rail) ||
      isCurveRailType(resolveRailType(rail)) ||
      isJunctionRailType(resolveRailType(rail)) ||
      edge.points.length !== 2
    ) {
      refinedEdges.push(edge);
      continue;
    }

    const [p0, p1] = edge.points;
    const vx = p1.x - p0.x;
    const vy = p1.y - p0.y;
    const len2 = vx * vx + vy * vy;
    if (len2 <= 1e-12) {
      refinedEdges.push(edge);
      continue;
    }

    const hits: { t: number; pt: Pt; key: string }[] = [];
    for (const att of attachments) {
      const t = Math.max(
        0,
        Math.min(1, ((att.pt.x - p0.x) * vx + (att.pt.y - p0.y) * vy) / len2)
      );
      const projX = p0.x + vx * t;
      const projY = p0.y + vy * t;
      const d2 =
        (att.pt.x - projX) * (att.pt.x - projX) + (att.pt.y - projY) * (att.pt.y - projY);
      if (d2 > attachTol2 || t <= 0.02 || t >= 0.98) continue;
      // Always split on the rail centreline. Using the raw connector mid (which can
      // sit up to ENDPOINT_TOLERANCE off-rail) kinked the polyline and yawed OHTs
      // ~10–20° while approaching / leaving the H-connector.
      hits.push({ t, pt: { x: projX, y: projY }, key: att.key });
    }

    if (hits.length === 0) {
      refinedEdges.push(edge);
      continue;
    }

    hits.sort((a, b) => a.t - b.t);
    const unique: { t: number; pt: Pt; key: string }[] = [];
    for (const hit of hits) {
      const prev = unique[unique.length - 1];
      if (prev && Math.abs(prev.t - hit.t) < 0.01) {
        // Two connector legs projecting to the same split — share the node.
        connectorLegNodes.set(hit.key, findOrAddNode(prev.pt));
        continue;
      }
      unique.push(hit);
    }

    for (const hit of unique) {
      connectorLegNodes.set(hit.key, findOrAddNode(hit.pt));
    }

    const chain: Pt[] = [p0, ...unique.map((h) => h.pt), p1];
    for (let i = 0; i < chain.length - 1; i++) {
      const segPoints = [chain[i], chain[i + 1]];
      const { cumLen, length } = cumulativeLengths(segPoints);
      if (length <= 0) continue;
      refinedEdges.push({
        id: edge.id,
        a: findOrAddNode(segPoints[0]),
        b: findOrAddNode(segPoints[1]),
        points: segPoints,
        cumLen,
        length,
        cumStart: edge.cumStart,
        switchMode: edge.switchMode,
      });
    }
  }

  // Exportable home↔far bridge — same role tip↔hub stubs play for circle junctions.
  // Browser chooseNextEdge skips pathKind:'connector' so local walkers never
  // patrol the crossbar; AMHS export includes these for backend lane changes.
  for (const rail of rails) {
    if (!isConnectorElement(rail)) continue;
    const homeMid = getConnectorHomeLegMid(rail);
    const farMid = getConnectorFarLegMid(rail);
    const homeNode =
      connectorLegNodes.get(`${rail.id}:home`) ?? findOrAddNode(homeMid);
    const farNode = connectorLegNodes.get(`${rail.id}:far`) ?? findOrAddNode(farMid);
    if (homeNode === farNode) continue;
    const points = [
      { x: nodes[homeNode].x, y: nodes[homeNode].y },
      { x: nodes[farNode].x, y: nodes[farNode].y },
    ];
    const { cumLen, length } = cumulativeLengths(points);
    if (length <= 0) continue;
    refinedEdges.push({
      id: rail.id,
      a: homeNode,
      b: farNode,
      points,
      cumLen,
      length,
      cumStart: 0,
      pathKind: 'connector',
    });
  }

  const nodeEdges: number[][] = nodes.map(() => []);
  refinedEdges.forEach((edge, idx) => {
    nodeEdges[edge.a].push(idx);
    if (edge.b !== edge.a) nodeEdges[edge.b].push(idx);
  });

  return { nodes, edges: refinedEdges, nodeEdges };
}

export interface EdgePosition {
  /** Point in world space. */
  x: number;
  y: number;
  /** Heading in degrees, pointing in the a -> b direction of the edge. */
  angle: number;
}

/** Returns the world point + heading at a given arc length along an edge. */
/**
 * Get the world position at a given arc length along an edge.
 * Interpolates between points using cumulative length lookup.
 * @param edge - Track edge with sampled points and cumulative lengths
 * @param dist - Arc length distance along the edge (0 to edge.length)
 * @returns Position and heading at the given distance
 */
export function pointAtDistance(edge: TrackEdge, dist: number): EdgePosition {
  const clamped = Math.max(0, Math.min(edge.length, dist));

  // Find the segment containing `clamped`.
  let i = 1;
  while (i < edge.cumLen.length && edge.cumLen[i] < clamped) i++;
  if (i >= edge.points.length) i = edge.points.length - 1;

  const p0 = edge.points[i - 1];
  const p1 = edge.points[i];
  const segLen = edge.cumLen[i] - edge.cumLen[i - 1] || 1;
  const t = (clamped - edge.cumLen[i - 1]) / segLen;

  return {
    x: p0.x + (p1.x - p0.x) * t,
    y: p0.y + (p1.y - p0.y) * t,
    angle: (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI,
  };
}

export interface GraphProjection {
  edgeIndex: number;
  dist: number; // arc length from edge.a
  distance: number; // perpendicular distance to the track
}

/** Projects a world point onto the nearest edge of the graph. */
/**
 * Project a point onto the nearest point on the track graph.
 * Searches all edges and returns the closest point with edge and distance info.
 * @param graph - Track graph
 * @param pt - Point to project
 * @returns Projection info with edge ID, position, and distance, or null if graph is empty
 */
export function projectOntoGraph(
  graph: TrackGraph,
  pt: Pt,
  options?: { excludeRailIds?: ReadonlySet<string> | string[] }
): GraphProjection | null {
  let best: GraphProjection | null = null;
  const exclude =
    options?.excludeRailIds == null
      ? null
      : options.excludeRailIds instanceof Set
        ? options.excludeRailIds
        : new Set(options.excludeRailIds);

  graph.edges.forEach((edge, edgeIndex) => {
    if (exclude?.has(edge.id)) return;
    for (let i = 1; i < edge.points.length; i++) {
      const p0 = edge.points[i - 1];
      const p1 = edge.points[i];
      const dx = p1.x - p0.x;
      const dy = p1.y - p0.y;
      const segLen2 = dx * dx + dy * dy;
      if (segLen2 === 0) continue;

      let t = ((pt.x - p0.x) * dx + (pt.y - p0.y) * dy) / segLen2;
      t = Math.max(0, Math.min(1, t));

      const px = p0.x + dx * t;
      const py = p0.y + dy * t;
      const distance = Math.hypot(pt.x - px, pt.y - py);

      if (!best || distance < best.distance) {
        best = {
          edgeIndex,
          dist: edge.cumLen[i - 1] + Math.sqrt(segLen2) * t,
          distance,
        };
      }
    }
  });

  return best;
}

/**
 * At `node`, having arrived travelling with `headingDeg`, choose the outgoing
 * edge that best continues that heading. Returns the chosen edge index and the
 * travel direction (+1 means move a -> b, -1 means b -> a), or null if none.
 *
 * `cameFromEdge` is avoided unless it is the only option (dead-end reversal).
 */
/** Straightest-first, falling back to overall direction when tangents tie. */
function byStraightness(
  a: { score: number; chordScore: number },
  b: { score: number; chordScore: number }
): number {
  if (Math.abs(b.score - a.score) > 1e-6) return b.score - a.score;
  return b.chordScore - a.chordScore;
}

export function chooseNextEdge(
  graph: TrackGraph,
  node: number,
  headingDeg: number,
  cameFromEdge: number,
  options?: { altBranch?: boolean }
): { edgeIndex: number; dir: 1 | -1; tookAltBranch?: boolean } | null {
  const incident = graph.nodeEdges[node];
  if (incident.length === 0) return null;

  const headRad = (headingDeg * Math.PI) / 180;
  const hx = Math.cos(headRad);
  const hy = Math.sin(headRad);

  const paths: Array<{
    edgeIndex: number;
    dir: 1 | -1;
    score: number;
    chordScore: number;
    railId: string;
  }> = [];

  for (const edgeIndex of incident) {
    const edge = graph.edges[edgeIndex];
    // H-connector bridges exist for AMHS export / backend routing. Local
    // walkers must not take them — lane changes stay animation-driven.
    if (edge.pathKind === 'connector') continue;
    const n = edge.points.length;

    // Determine outgoing heading and travel direction leaving this node.
    // `c*` is the chord to the far end: every through-path of a circular
    // junction leaves a port along the same spoke tangent, so the immediate
    // tangent alone cannot tell them apart — the chord says where each
    // actually goes.
    const candidates: Array<{ dir: 1 | -1; ox: number; oy: number; cx: number; cy: number }> = [];
    if (edge.a === node) {
      const p0 = edge.points[0];
      const p1 = edge.points[1];
      const far = edge.points[n - 1];
      candidates.push({
        dir: 1,
        ox: p1.x - p0.x,
        oy: p1.y - p0.y,
        cx: far.x - p0.x,
        cy: far.y - p0.y,
      });
    }
    if (edge.b === node && !edge.isUnidirectional) {
      const p0 = edge.points[n - 1];
      const p1 = edge.points[n - 2];
      const far = edge.points[0];
      candidates.push({
        dir: -1,
        ox: p1.x - p0.x,
        oy: p1.y - p0.y,
        cx: far.x - p0.x,
        cy: far.y - p0.y,
      });
    }

    for (const c of candidates) {
      const len = Math.hypot(c.ox, c.oy) || 1;
      const chordLen = Math.hypot(c.cx, c.cy) || 1;
      const score = (c.ox / len) * hx + (c.oy / len) * hy;
      const chordScore = (c.cx / chordLen) * hx + (c.cy / chordLen) * hy;
      const isCameFrom = edgeIndex === cameFromEdge;
      // Penalise reversing back along the edge we just travelled.
      const adjScore = isCameFrom ? score - 10 : score;
      paths.push({
        edgeIndex,
        dir: c.dir,
        score: adjScore,
        chordScore: isCameFrom ? chordScore - 10 : chordScore,
        railId: edge.id,
      });
    }
  }

  if (paths.length === 0) return null;

  paths.sort(byStraightness);

  // Junctions configured in diverge mode should take the branch path by default
  // rather than continuing straight through the same junction rail.
  // Turntables are NOT diverge forks — free hub hopping looks like track jumping;
  // lane changes belong on H-connectors. Prefer straightest heading instead.
  const cameFrom = graph.edges[cameFromEdge];
  if (cameFrom?.switchMode === 'diverge' && cameFrom.pathKind !== 'turntable') {
    const sameRailOptions = paths
      .filter((p) => p.railId === cameFrom.id && p.edgeIndex !== cameFromEdge)
      .sort(byStraightness);
    if (sameRailOptions.length >= 2) {
      // Prefer a true branch over straight-through: pick the least-straight
      // option that is still not a near U-turn.
      const nonReverse = sameRailOptions.filter((p) => p.score > -0.2);
      const primaryDiverge =
        (nonReverse.length > 0
          ? nonReverse.slice().sort((a, b) => a.score - b.score)[0]
          : sameRailOptions[1]);
      const secondary = sameRailOptions.find((p) => p.edgeIndex !== primaryDiverge.edgeIndex);

      // On fork-like ties, allow caller-provided altBranch to pick the
      // sibling branch so vehicles can alternate ends over time.
      const useAlternateSibling =
        options?.altBranch === true &&
        !!secondary &&
        Math.abs((secondary?.score ?? 0) - primaryDiverge.score) < 0.2;
      const divergeCandidate = useAlternateSibling && secondary ? secondary : primaryDiverge;

      return {
        edgeIndex: divergeCandidate.edgeIndex,
        dir: divergeCandidate.dir,
        tookAltBranch: true,
      };
    }
  }

  if (options?.altBranch && paths.length > 1 && paths[1].score > -5) {
    // Don't alt-branch across turntable stubs (same as diverge skip above).
    if (cameFrom?.pathKind !== 'turntable') {
      return { edgeIndex: paths[1].edgeIndex, dir: paths[1].dir, tookAltBranch: true };
    }
  }

  const best = paths[0];
  const reverse = paths.find((p) => p.edgeIndex === cameFromEdge);

  // Open-end guard: endpoint clustering can merge unrelated rail tips within
  // ENDPOINT_TOLERANCE. Prefer turning around over hopping onto another rail
  // that is only nearby (esp. a parallel lane). Lane changes use H-connectors.
  // Intentional junctions (turntable) may still transfer between stubs.
  if (
    reverse &&
    best.edgeIndex !== cameFromEdge &&
    best.railId !== cameFrom?.id
  ) {
    const bestEdge = graph.edges[best.edgeIndex];
    const intentionalJunction =
      cameFrom?.pathKind === 'turntable' ||
      bestEdge?.pathKind === 'turntable' ||
      cameFrom?.pathKind === 'roundabout' ||
      bestEdge?.pathKind === 'roundabout';

    if (!intentionalJunction) {
      // Weak forward continuation → open end, reverse.
      if (best.score < 0.55) {
        return { edgeIndex: reverse.edgeIndex, dir: reverse.dir, tookAltBranch: false };
      }

      // Strong heading but parallel-lane false merge: tips share a node while
      // centerlines stay laterally offset. Reverse instead of jumping lanes.
      const laneOffset = lateralLaneOffsetAtNode(
        graph,
        node,
        cameFromEdge,
        best.edgeIndex
      );
      if (laneOffset > 3.5) {
        return { edgeIndex: reverse.edgeIndex, dir: reverse.dir, tookAltBranch: false };
      }
    }
  }

  return { edgeIndex: best.edgeIndex, dir: best.dir, tookAltBranch: false };
}

/** Lateral separation of two edges' centerlines at a shared node (world units). */
function lateralLaneOffsetAtNode(
  graph: TrackGraph,
  node: number,
  edgeIndexA: number,
  edgeIndexB: number
): number {
  const a = graph.edges[edgeIndexA];
  const b = graph.edges[edgeIndexB];
  if (!a || !b || a.points.length < 2 || b.points.length < 2) return 0;

  const n = graph.nodes[node];
  if (!n) return 0;

  const awayPoint = (edge: TrackEdge): Pt => {
    const p0 = edge.points[0];
    const p1 = edge.points[edge.points.length - 1];
    const d0 = Math.hypot(p0.x - n.x, p0.y - n.y);
    const d1 = Math.hypot(p1.x - n.x, p1.y - n.y);
    // Point farther from the node along this edge.
    return d0 >= d1 ? p0 : p1;
  };

  const pa = awayPoint(a);
  const pb = awayPoint(b);
  // Direction of travel leaving the node along edge A toward its far end…
  // Use average chord A→B of far points relative to heading of A.
  const ax = pa.x - n.x;
  const ay = pa.y - n.y;
  const alen = Math.hypot(ax, ay) || 1;
  const ux = ax / alen;
  const uy = ay / alen;
  // Perpendicular
  const px = -uy;
  const py = ux;
  // Lateral offset of B's far point from A's centerline through the node.
  return Math.abs((pb.x - n.x) * px + (pb.y - n.y) * py);
}

function shortestDistanceFromNodeToStop(
  graph: TrackGraph,
  startNode: number,
  targetRailId: string,
  targetDistance: number
): number {
  const nodeCount = graph.nodes.length;
  if (startNode < 0 || startNode >= nodeCount) return Infinity;

  const dist = Array.from({ length: nodeCount }, () => Infinity);
  const visited = Array.from({ length: nodeCount }, () => false);
  dist[startNode] = 0;

  for (let step = 0; step < nodeCount; step++) {
    let node = -1;
    let best = Infinity;
    for (let i = 0; i < nodeCount; i++) {
      if (!visited[i] && dist[i] < best) {
        best = dist[i];
        node = i;
      }
    }
    if (node < 0) break;
    visited[node] = true;

    for (const edgeIndex of graph.nodeEdges[node]) {
      const edge = graph.edges[edgeIndex];
      if (edge.pathKind === 'connector') continue;
      if (edge.isUnidirectional && edge.b === node) continue;
      const other = edge.a === node ? edge.b : edge.a;
      const nextDist = dist[node] + edge.length;
      if (nextDist < dist[other]) dist[other] = nextDist;
    }
  }

  let bestToStop = Infinity;
  for (const edge of graph.edges) {
    if (edge.id !== targetRailId) continue;

    // Check if the target distance falls within this particular segment.
    const localDist = targetDistance - edge.cumStart;
    if (localDist < -0.001 || localDist > edge.length + 0.001) continue;

    const clampedLocal = Math.max(0, Math.min(edge.length, localDist));
    bestToStop = Math.min(
      bestToStop,
      dist[edge.a] + clampedLocal,
      dist[edge.b] + edge.length - clampedLocal
    );
  }

  return bestToStop;
}

function shortestDistanceFromNodeToRail(
  graph: TrackGraph,
  startNode: number,
  targetRailId: string
): number {
  const nodeCount = graph.nodes.length;
  if (startNode < 0 || startNode >= nodeCount) return Infinity;

  const dist = Array.from({ length: nodeCount }, () => Infinity);
  const visited = Array.from({ length: nodeCount }, () => false);
  dist[startNode] = 0;

  for (let step = 0; step < nodeCount; step++) {
    let node = -1;
    let best = Infinity;
    for (let i = 0; i < nodeCount; i++) {
      if (!visited[i] && dist[i] < best) {
        best = dist[i];
        node = i;
      }
    }
    if (node < 0) break;
    visited[node] = true;

    for (const edgeIndex of graph.nodeEdges[node]) {
      const edge = graph.edges[edgeIndex];
      if (edge.pathKind === 'connector') continue;
      if (edge.isUnidirectional && edge.b === node) continue;
      const other = edge.a === node ? edge.b : edge.a;
      const nextDist = dist[node] + edge.length;
      if (nextDist < dist[other]) dist[other] = nextDist;
    }
  }

  let bestToRail = Infinity;
  for (const edge of graph.edges) {
    if (edge.id !== targetRailId) continue;
    bestToRail = Math.min(bestToRail, dist[edge.a], dist[edge.b]);
  }

  return bestToRail;
}

/**
 * Choose the outgoing edge that best leads to a target rail id.
 * Useful for route-following when the target rail is not directly incident.
 */
export function chooseNextEdgeTowardRail(
  graph: TrackGraph,
  node: number,
  cameFromEdge: number,
  headingDeg: number,
  targetRailId: string
): { edgeIndex: number; dir: 1 | -1 } | null {
  const incident = graph.nodeEdges[node];
  if (incident.length === 0) return null;

  const headRad = (headingDeg * Math.PI) / 180;
  const hx = Math.cos(headRad);
  const hy = Math.sin(headRad);

  let best: { edgeIndex: number; dir: 1 | -1; totalCost: number; isCameFrom: boolean } | null = null;

  for (const edgeIndex of incident) {
    const edge = graph.edges[edgeIndex];
    if (edge.pathKind === 'connector') continue;
    const candidates: Array<{ dir: 1 | -1; nextNode: number; travel: number; ox: number; oy: number }> = [];
    if (edge.a === node) {
      const p1 = edge.points[1] || edge.points[0];
      const p0 = edge.points[0];
      candidates.push({ dir: 1, nextNode: edge.b, travel: edge.length, ox: p1.x - p0.x, oy: p1.y - p0.y });
    }
    if (edge.b === node && !edge.isUnidirectional) {
      const n = edge.points.length;
      const p1 = edge.points[n - 2] || edge.points[n - 1];
      const p0 = edge.points[n - 1];
      candidates.push({ dir: -1, nextNode: edge.a, travel: edge.length, ox: p1.x - p0.x, oy: p1.y - p0.y });
    }

    for (const candidate of candidates) {
      const isCameFrom = edgeIndex === cameFromEdge;

      let distCost = Infinity;
      if (edge.id === targetRailId) {
        distCost = 0;
      } else {
        const d = shortestDistanceFromNodeToRail(graph, candidate.nextNode, targetRailId);
        if (Number.isFinite(d)) {
          distCost = candidate.travel + d;
        }
      }

      if (!Number.isFinite(distCost)) continue;

      const len = Math.hypot(candidate.ox, candidate.oy) || 1;
      const cosTheta = (candidate.ox / len) * hx + (candidate.oy / len) * hy;
      const headingPenalty = (1.0 - cosTheta) * 500;
      const reversePenalty = isCameFrom ? 10000 : 0;
      const totalCost = distCost + headingPenalty + reversePenalty;

      if (
        !best ||
        totalCost < best.totalCost ||
        (Math.abs(totalCost - best.totalCost) < 0.1 && best.isCameFrom && !isCameFrom)
      ) {
        best = { edgeIndex, dir: candidate.dir, totalCost, isCameFrom };
      }
    }
  }

  return best ? { edgeIndex: best.edgeIndex, dir: best.dir } : null;
}

/**
 * At a junction, choose the outgoing edge that minimizes travel distance to a
 * target stop. Falls back to heading-based routing when the stop is unreachable.
 * Penalizes reversing at junctions to favor natural loop flow.
 */
export function chooseNextEdgeTowardStop(
  graph: TrackGraph,
  node: number,
  cameFromEdge: number,
  headingDeg: number,
  targetRailId: string,
  targetDistance: number
): { edgeIndex: number; dir: 1 | -1 } | null {
  const incident = graph.nodeEdges[node];
  if (incident.length === 0) return null;

  const headRad = (headingDeg * Math.PI) / 180;
  const hx = Math.cos(headRad);
  const hy = Math.sin(headRad);

  let best: { edgeIndex: number; dir: 1 | -1; totalCost: number; isCameFrom: boolean } | null = null;
  let hasReachableCandidate = false;

  // Build distance map from EVERY candidate next-node once
  const nodeDistances = new Map<number, number>();

  for (const edgeIndex of incident) {
    const edge = graph.edges[edgeIndex];
    if (edge.pathKind === 'connector') continue;
    if (edge.a === node) {
      if (!nodeDistances.has(edge.b)) {
        nodeDistances.set(edge.b, shortestDistanceFromNodeToStop(graph, edge.b, targetRailId, targetDistance));
      }
    }
    if (edge.b === node) {
      if (!nodeDistances.has(edge.a)) {
        nodeDistances.set(edge.a, shortestDistanceFromNodeToStop(graph, edge.a, targetRailId, targetDistance));
      }
    }
  }

  for (const edgeIndex of incident) {
    const edge = graph.edges[edgeIndex];
    if (edge.pathKind === 'connector') continue;
    const candidates: Array<{ dir: 1 | -1; nextNode: number; travel: number; ox: number; oy: number }> = [];
    if (edge.a === node) {
      const p1 = edge.points[1] || edge.points[0];
      const p0 = edge.points[0];
      candidates.push({ dir: 1, nextNode: edge.b, travel: edge.length, ox: p1.x - p0.x, oy: p1.y - p0.y });
    }
    if (edge.b === node && !edge.isUnidirectional) {
      const n = edge.points.length;
      const p1 = edge.points[n - 2] || edge.points[n - 1];
      const p0 = edge.points[n - 1];
      candidates.push({ dir: -1, nextNode: edge.a, travel: edge.length, ox: p1.x - p0.x, oy: p1.y - p0.y });
    }

    for (const candidate of candidates) {
      const isCameFrom = edgeIndex === cameFromEdge;

      // Distance cost from Dijkstra.
      let distCost = Infinity;
      if (edge.id === targetRailId) {
        const localDist = targetDistance - edge.cumStart;
        if (localDist >= -0.001 && localDist <= edge.length + 0.001) {
          // If we are ON the target edge, cost is just the distance to the point
          distCost = candidate.dir > 0
            ? Math.max(0, Math.min(edge.length, localDist))
            : edge.length - Math.max(0, Math.min(edge.length, localDist));
        }
      }

      if (!Number.isFinite(distCost)) {
        const d = nodeDistances.get(candidate.nextNode) ?? Infinity;
        if (d !== Infinity) {
          distCost = candidate.travel + d;
        }
      }

      if (!Number.isFinite(distCost)) continue;

      hasReachableCandidate = true;

      // Heading penalty cost: for stop-directed routing, keep this small so
      // shortest-path distance to the target stop dominates branch selection.
      const len = Math.hypot(candidate.ox, candidate.oy) || 1;
      const cosTheta = (candidate.ox / len) * hx + (candidate.oy / len) * hy;
      // Map cosTheta from [1, -1] (straight to reverse) to [0, 40] penalty units.
      const headingPenalty = (1.0 - cosTheta) * 20;
      const reversePenalty = isCameFrom ? 10000 : 0; // Heavy penalty for literal U-turn on same edge.

      const totalCost = distCost + headingPenalty + reversePenalty;

      if (
        !best ||
        totalCost < best.totalCost ||
        (Math.abs(totalCost - best.totalCost) < 0.1 && best.isCameFrom && !isCameFrom)
      ) {
        best = { edgeIndex, dir: candidate.dir, totalCost, isCameFrom };
      }
    }
  }

  if (!hasReachableCandidate) return null;

  return best ? { edgeIndex: best.edgeIndex, dir: best.dir } : null;
}

// ─── Route enumeration ──────────────────────────────────────────────────────

export interface RouteOption {
  /** Ordered rail element IDs to traverse (consecutive duplicates collapsed). */
  railIds: string[];
  /** Total centreline length in world units. */
  length: number;
  /** Graph-edge hop count. */
  hops: number;
}

const DEFAULT_MAX_ROUTES = 40;
const DEFAULT_MAX_ROUTE_HOPS = 80;

/** Index of the graph node nearest a world-space point (straight-line distance). */
/**
 * Find the graph node nearest to a given point (may be off-track).
 * @param graph - Track graph
 * @param pt - Point to search from
 * @returns Index of nearest node, or null if graph has no nodes
 */
export function findNearestNode(graph: TrackGraph, pt: Pt): number | null {
  let best = -1;
  let bestDist = Infinity;
  graph.nodes.forEach((n, i) => {
    const d = Math.hypot(n.x - pt.x, n.y - pt.y);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  });
  return best >= 0 ? best : null;
}

/** Node nearest a world point, biased toward whichever end of the nearest edge is closer. */
/**
 * Find the graph node nearest to a given point, preferring nodes directly on track.
 * @param graph - Track graph
 * @param pt - Point to search from
 * @returns Index of nearest node on track, or null if graph has no nodes
 */
export function findNearestNodeOnTrack(graph: TrackGraph, pt: Pt): number | null {
  const proj = projectOntoGraph(graph, pt);
  if (!proj) return findNearestNode(graph, pt);
  const edge = graph.edges[proj.edgeIndex];
  return proj.dist <= edge.length - proj.dist ? edge.a : edge.b;
}

function buildRouteOption(graph: TrackGraph, edgePath: number[]): RouteOption {
  const railIds: string[] = [];
  let length = 0;
  for (const idx of edgePath) {
    const edge = graph.edges[idx];
    length += edge.length;
    if (railIds[railIds.length - 1] !== edge.id) railIds.push(edge.id);
  }
  return { railIds, length, hops: edgePath.length };
}

/**
 * Enumerates every simple path (no repeated node, so no doubling back through a
 * loop) between two nodes of the track graph. Results are sorted shortest-first
 * and capped so a densely-looped layout can't blow up combinatorially.
 */
export function enumerateSimplePaths(
  graph: TrackGraph,
  startNode: number,
  endNode: number,
  options?: { maxResults?: number; maxHops?: number }
): RouteOption[] {
  if (
    startNode < 0 ||
    endNode < 0 ||
    startNode >= graph.nodes.length ||
    endNode >= graph.nodes.length
  ) {
    return [];
  }

  const maxResults = options?.maxResults ?? DEFAULT_MAX_ROUTES;
  const maxHops = options?.maxHops ?? DEFAULT_MAX_ROUTE_HOPS;

  if (startNode === endNode) {
    return [{ railIds: [], length: 0, hops: 0 }];
  }

  const results: RouteOption[] = [];
  const visited = new Set<number>([startNode]);
  const edgePath: number[] = [];

  const dfs = (node: number) => {
    if (results.length >= maxResults || edgePath.length >= maxHops) return;

    for (const edgeIndex of graph.nodeEdges[node]) {
      const edge = graph.edges[edgeIndex];
      if (edge.isUnidirectional && edge.b === node) continue;
      const other = edge.a === node ? edge.b : edge.a;
      if (other === node || visited.has(other)) continue;

      edgePath.push(edgeIndex);
      visited.add(other);

      if (other === endNode) {
        results.push(buildRouteOption(graph, edgePath));
      } else {
        dfs(other);
      }

      visited.delete(other);
      edgePath.pop();

      if (results.length >= maxResults) return;
    }
  };

  dfs(startNode);
  results.sort((a, b) => a.length - b.length);
  return results;
}
